import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { Input, ViewEncapsulation, Component, NgModule } from '@angular/core';
import * as i2 from '@engineering11/ui-lib/e11-directives';
import { E11DirectivesModule } from '@engineering11/ui-lib/e11-directives';

/**
 * A badge component that can be used to display a badge with a value.
 * @example
 * <e11-badge value="5"></e11-badge>
 * @example
 * <e11-badge value="10" limit="5"></e11-badge>
 * @example
 * <e11-badge value="10" limit="5" color="success"></e11-badge>
 * @example
 * <e11-badge value="10" limit="5" color="success" shape="square"></e11-badge>
 */
class E11BadgeComponent {
    constructor() {
        /**
         * The color of the badge. Defaults to 'error'.
         * @type {Color}
         * @default 'error'
         */
        this.color = 'error';
        /**
         * The shape of the badge. Can be 'circular' or 'square'. Defaults to 'circular'.
         * @type {string}
         * @default 'circular'
         */
        this.shape = 'circular';
        /**
         * The size of the badge. Can be 'xs' or 'square'. Defaults to 'xs'.
         * @type {Size}
         * @default 'xs'
         */
        this.size = 'xs';
        /**
         * Indicates if the badge is in a tag. Defaults to false.
         * @type {boolean}
         * @default false
         */
        this.inTag = false;
        /**
         * Adjusts the Y position of the badge.
         * @type {string|undefined}
         * @default ''
         */
        this.posAdjustY = '';
        /**
         * Adjusts the X position of the badge.
         * @type {string|undefined}
         * @default ''
         */
        this.posAdjustX = '';
        /**
         * Indicates if the badge is displayed inline. Defaults to false.
         * @type {boolean}
         * @default false
         */
        this.inline = false;
        /**
         * The title text of the badge. Defaults to an empty string.
         * @type {string|undefined}
         * @default ''
         */
        this.titleText = '';
        /**
         * The position of the title text. Defaults to 'top'.
         * @type {string|undefined}
         * @default 'top'
         */
        this.titleTextPosition = 'top';
    }
    /**
     * Sets the value of the badge and updates the display value.
     * @param {number} _value - The value to set.
     */
    set value(_value) {
        this._value = _value;
        this.valueDisplay = _value.toString();
        this.limitValue(_value, this._limit);
    }
    /**
     * Sets the limit for the badge value and updates the display value if necessary.
     * @param {number|undefined} _limit - The limit to set.
     */
    set limit(_limit) {
        this._limit = _limit;
        this.limitValue(this._value, this._limit);
    }
    /**
     * Limits the value of the badge if it exceeds the specified limit.
     * @private
     * @param {number|undefined} value - The value to limit.
     * @param {number|undefined} limit - The limit to apply.
     */
    limitValue(value, limit) {
        if (!value || !limit) {
            return;
        }
        if (value > limit) {
            this.valueDisplay = limit.toString() + '+';
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BadgeComponent, deps: [], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11BadgeComponent, isStandalone: true, selector: "e11-badge", inputs: { color: "color", shape: "shape", size: "size", inTag: "inTag", classOverrides: "classOverrides", class: "class", posAdjustY: "posAdjustY", posAdjustX: "posAdjustX", containerClassOverrides: "containerClassOverrides", containerClass: "containerClass", inline: "inline", titleText: "titleText", titleTextPosition: "titleTextPosition", value: "value", limit: "limit" }, ngImport: i0, template: `
    <!-- TODO Remove containerClassOverrides on the next major release -->
    <div class="e11-badge-container {{ containerClassOverrides }} {{ containerClass }}">
      <!-- TODO Remove classOverride on the next major release -->
      <div
        class="e11-badge e11-z-10 e11-inline-flex e11-items-center e11-text-xs e11-px-1 e11-font-medium e11-text-white
e11-badge-color-{{ color }} e11-badge-size-{{ size }} {{ classOverrides }} {{ class }}"
        [ngClass]="{ 'e11-rounded-full': shape === 'circular', 'e11-absolute e11-left-full e11--top-3 ': !inline }"
        [e11title]="titleText"
        [e11titlePosition]="titleTextPosition"
        [ngStyle]="{ left: 'calc(100% + ' + posAdjustX + ')', top: posAdjustY }"
      >
        {{ valueDisplay }}
      </div>
    </div>
  `, isInline: true, styles: [".e11-badge{display:flex;align-items:center;justify-content:center}.e11-badge.e11-badge-size-xs{height:18px;min-width:18px}.e11-badge.e11-badge-size-sm{height:22px;min-width:22px}.e11-badge.e11-badge-size-md{height:28px;min-width:28px}.e11-badge.e11-badge-size-lg{height:32px;min-width:32px}\n"], dependencies: [{ kind: "ngmodule", type: CommonModule }, { kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "ngmodule", type: E11DirectivesModule }, { kind: "directive", type: i2.E11TitleDirective, selector: "[e11title]", inputs: ["e11title", "e11titlePosition", "delay"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BadgeComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-badge', standalone: true, encapsulation: ViewEncapsulation.None, imports: [CommonModule, E11DirectivesModule], template: `
    <!-- TODO Remove containerClassOverrides on the next major release -->
    <div class="e11-badge-container {{ containerClassOverrides }} {{ containerClass }}">
      <!-- TODO Remove classOverride on the next major release -->
      <div
        class="e11-badge e11-z-10 e11-inline-flex e11-items-center e11-text-xs e11-px-1 e11-font-medium e11-text-white
e11-badge-color-{{ color }} e11-badge-size-{{ size }} {{ classOverrides }} {{ class }}"
        [ngClass]="{ 'e11-rounded-full': shape === 'circular', 'e11-absolute e11-left-full e11--top-3 ': !inline }"
        [e11title]="titleText"
        [e11titlePosition]="titleTextPosition"
        [ngStyle]="{ left: 'calc(100% + ' + posAdjustX + ')', top: posAdjustY }"
      >
        {{ valueDisplay }}
      </div>
    </div>
  `, styles: [".e11-badge{display:flex;align-items:center;justify-content:center}.e11-badge.e11-badge-size-xs{height:18px;min-width:18px}.e11-badge.e11-badge-size-sm{height:22px;min-width:22px}.e11-badge.e11-badge-size-md{height:28px;min-width:28px}.e11-badge.e11-badge-size-lg{height:32px;min-width:32px}\n"] }]
        }], propDecorators: { color: [{
                type: Input
            }], shape: [{
                type: Input
            }], size: [{
                type: Input
            }], inTag: [{
                type: Input
            }], classOverrides: [{
                type: Input
            }], class: [{
                type: Input
            }], posAdjustY: [{
                type: Input
            }], posAdjustX: [{
                type: Input
            }], containerClassOverrides: [{
                type: Input
            }], containerClass: [{
                type: Input
            }], inline: [{
                type: Input
            }], titleText: [{
                type: Input
            }], titleTextPosition: [{
                type: Input
            }], value: [{
                type: Input
            }], limit: [{
                type: Input
            }] } });

class E11BadgeModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BadgeModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11BadgeModule, imports: [E11BadgeComponent], exports: [E11BadgeComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BadgeModule, imports: [E11BadgeComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11BadgeModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [E11BadgeComponent],
                    exports: [E11BadgeComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11BadgeComponent, E11BadgeModule };
